import { useDismiss, useFloatingRootContext, useInteractions, useRole } from '@floating-ui/react';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Checkbox } from '@hologram-dimension/checkbox';
import { Chip } from '@hologram-dimension/chip';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Popover } from '@hologram-dimension/popover';
import { TextInput } from '@hologram-dimension/text-input';
import useBulkTagCall from 'api/bulkActions/useBulkTagCall';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useDeleteTagCall from 'api/legacy/tags/useDeleteTagCall';
import useTagsWithDetails from 'api/tags/useTagsWithDetails';
import clsx from 'clsx/lite';
import Pictogram from 'components/Pictogram/Pictogram';
import Table from 'components/Table/Table';
import type { HeaderItem, TableCellItem, TableRowItem } from 'components/Table/types';
import useUserInfo from 'hooks/User/useUserInfo';
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  StructsBulkTagSimsResponse,
  StructsGetTagsResponse,
  StructsTagResponseWithDetails,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSubmit, TriggerRef } from 'types/common';
import { useDebounceCallback, useUrl } from 'utils/hooks';
import currentLocale from 'utils/locale/currentLocale';
import alphabeticSortCompare from 'utils/strings/alphabeticSortCompare';
import TagDeleteConfirmation from './TagDeleteConfirmation';
import TagEditor from './TagEditor';
import styles from './TagManager.module.css';
import type { TagManagerTag } from './types';

interface SearchFormProps {
  searchText: string;
}

interface TagManagerProps {
  /**
   * Turns the tag manager into a selection form.
   */
  selectedSimIds?: HologramSimIdsMap;
  /**
   * Organization ids to filter tags by
   */
  organizationIds?: Array<OrgId>;
  /**
   * Optional className for style overrides
   */
  className?: string;
  /**
   * On processing state change
   */
  onProcessing?: (value: boolean) => void;
  /**
   * Handle success
   */
  onSuccess?: (hologramSimIds?: Array<HologramSimId>) => void;
  /**
   * Handle errors
   */
  onError?: (error?: string) => void;
  /**
   * Optional, handle committed (synced) changes to tags
   * @param lastTags The tags updated to be used to create `useTags*` mutates
   */
  onTagsMutated?: (lastTags: Array<StructsTagResponseWithDetails>) => void;
  /**
   * Optional, to specify whether its the standalone or modal variant (for styling)
   */
  variant?: 'modal' | 'standalone';
  /**
   * SIM Inventory base URL for routing purposes
   */
  simInventoryBaseUrl: string;
  /**
   * Turns on a selection mode. Changes to tags persist but linkages to SIMs won't be committed until later (e.g. activation)
   */
  tagSelectMode?: boolean;
  /**
   * (Optional) Array of tags to select by default
   */
  defaultSelection?: Array<TagId>;
  /**
   * If tag select mode is enabled, this function is called when the tag selection changes
   */
  onTagSelectionChanged?: (selectedTags: Array<TagId>) => void;
}

enum CheckedState {
  Checked,
  Indeterminate,
  Unchecked,
}

const getTagCheckedState = (tag: TagManagerTag, selectedSimIds: HologramSimIdsMap) => {
  if (tag.selectedSimCount === 0) {
    return CheckedState.Unchecked;
  }
  if (tag.selectedSimCount === selectedSimIds.size) {
    return CheckedState.Checked;
  }

  return CheckedState.Indeterminate;
};

const isTagManagerTag = (tag: any): tag is TagManagerTag => 'selectedSimCount' in tag;

const TagManager = forwardRef<OnSubmit, TagManagerProps>(
  (
    {
      selectedSimIds = new Map(),
      organizationIds: inputOrganizationIds,
      className,
      onProcessing,
      onSuccess,
      onError,
      onTagsMutated,
      variant = 'standalone',
      simInventoryBaseUrl,
      tagSelectMode = false,
      onTagSelectionChanged,
      defaultSelection,
    },
    ref
  ) => {
    /* ===== State ===== */
    const [addingTag, setAddingTag] = useState(false);
    const [editingTag, setEditingTag] = useState<TagManagerTag | null>(null);
    const [deletingTag, setDeletingTag] = useState<TagManagerTag | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);
    const [pendingLink, setPendingLink] = useState<Map<TagId, TagManagerTag>>(new Map());
    const [hasSetDefault, setHasSetDefaults] = useState(false);
    const [pendingUnlink, setPendingUnlink] = useState<Map<TagId, TagManagerTag>>(new Map());
    const [debouncingChanges, setDebouncingChanges] = useState<Array<Function>>([]);

    const singleSimMode = selectedSimIds.size === 1;
    const hasSelectedSims = selectedSimIds.size > 0;
    const deviceIds = useMemo(() => [...selectedSimIds.values()].sort(), [selectedSimIds]);

    const organizationId = useOrganization();
    const { organizationRole, releaseFlags } = useUserInfo();
    const canEdit = ['admin', 'manager', 'editor'].includes(organizationRole);
    const hasPreflight = releaseFlags.has(ReleaseFlag.FEATURE_PREDEPLOYMENT);
    const organizationIds = useMemo(() => {
      if (
        organizationId !== undefined &&
        (!inputOrganizationIds || inputOrganizationIds.length === 0)
      ) {
        return [organizationId];
      }

      return inputOrganizationIds;
    }, [organizationId, inputOrganizationIds]);

    const { sendAnalyticsEvent } = useAnalytics();

    const userOrgId = useOrganization();
    const activeSimsUrl = useUrl({
      url: `${simInventoryBaseUrl}/active`,
      replacers: [{ placeholder: 'orgid', value: userOrgId?.toString() ?? '' }],
    });
    const predeploymentSimsUrl = useUrl({
      url: `${simInventoryBaseUrl}/predeployment`,
      replacers: [{ placeholder: 'orgid', value: userOrgId?.toString() ?? '' }],
    });

    /* ===== Search ===== */
    const {
      watch: searchFormWatch,
      register: searchFormRegister,
      reset: searchFormReset,
    } = useForm<SearchFormProps>({
      defaultValues: { searchText: '' },
      mode: 'onChange',
    });

    const { searchText } = searchFormWatch();

    /* ===== Tags ===== */
    const {
      tags: tagsUnprocessed,
      isLoading,
      error,
      mutate,
    } = useTagsWithDetails({
      filters: {
        organizationIds,
      },
      canLoad: !!organizationIds && organizationIds.length > 0,
    });

    useEffect(() => {
      if (onTagsMutated) {
        onTagsMutated(tagsUnprocessed);
      }
    }, [onTagsMutated, tagsUnprocessed]);

    // TODO: Consider improving the API to allow filtering the COUNT by SIM IDs, but not the tag (in other words keep 0-count tags)
    // Alternatively, we could call the `useTags` API with a sim filter and without, and that will allow us to get ALL tags but also the sim counts. See which is faster
    // Additionally, if we DO switch back to the lighter `useTags` API, we'll need to write a SIM Inventory endpoint to wrap adding a tag so we can auto-link hologram SIM IDs instead of device IDs
    const tags = useMemo<Array<TagManagerTag>>(() => {
      let newTags;
      if (hasSelectedSims) {
        newTags = structuredClone(tagsUnprocessed).map((tag) => {
          // Work around for mutated return
          if (isTagManagerTag(tag)) {
            return tag as unknown as TagManagerTag;
          }
          if (!tag.id) {
            return {
              ...tag,
              selectedSimCount: 0,
            };
          }
          const selectedSimCount =
            tag.sims?.filter((sim) => sim.id && selectedSimIds.has(sim.id)).length ?? 0;
          return {
            ...tag,
            selectedSimCount,
          };
        });
      } else {
        newTags = structuredClone(tagsUnprocessed).map((tag) => ({
          ...tag,
          selectedSimCount: tag.sims?.length ?? 0,
        }));
      }

      const newDeviceIds = new Set<DeviceId>();

      newTags.forEach((tag) => {
        tag.sims?.forEach((sim) => {
          if (sim.claimedDeviceId && sim.id && selectedSimIds.has(sim.id)) {
            newDeviceIds.add(sim.claimedDeviceId);
          }
        });
      });

      return newTags;
    }, [hasSelectedSims, selectedSimIds, tagsUnprocessed]);

    const { sortedTags, usedTagNames } = useMemo(
      () => ({
        sortedTags:
          hasSelectedSims || tagSelectMode
            ? tags.sort((a, b) => {
                const simCountA = a.selectedSimCount ?? 0;
                const simCountB = b.selectedSimCount ?? 0;

                return simCountA === simCountB
                  ? alphabeticSortCompare(a.name ?? '', b.name ?? '')
                  : simCountB - simCountA;
              })
            : tags.sort((a, b) => alphabeticSortCompare(a.name ?? '', b.name ?? '')),
        usedTagNames: new Set(
          tags
            .filter((tag) => (tag.name?.length ?? 0) > 0)
            .map((tag) => tag.name?.toLocaleLowerCase(currentLocale)!)
        ),
      }),
      [hasSelectedSims, tagSelectMode, tags]
    );

    const filteredTags = useMemo(() => {
      if (!searchText) {
        return sortedTags;
      }

      const search = searchText.toLowerCase().trim();

      return sortedTags.filter((tag) => tag.name?.toLowerCase().includes(search));
    }, [searchText, sortedTags]);

    /* ====== Tag Changes ===== */
    const changeTag = useCallback(
      (tag: TagManagerTag, checked: boolean) => {
        if (!tag.id) {
          return;
        }

        const defaultCheckedState = getTagCheckedState(tag, selectedSimIds);

        if (checked) {
          if (defaultCheckedState !== CheckedState.Checked) {
            setPendingLink(new Map(pendingLink.set(tag.id, tag)));
          }
          if (pendingUnlink.delete(tag.id)) {
            setPendingUnlink(new Map(pendingUnlink));
          }
        } else {
          if (defaultCheckedState !== CheckedState.Unchecked) {
            setPendingUnlink(new Map(pendingUnlink.set(tag.id, tag)));
          }
          if (pendingLink.delete(tag.id)) {
            setPendingLink(new Map(pendingLink));
          }
        }
      },
      [pendingLink, pendingUnlink, selectedSimIds]
    );

    useEffect(() => {
      if (hasSetDefault) {
        return;
      }
      if ((defaultSelection?.length ?? 0) > 0 && tags.length > 0) {
        setHasSetDefaults(true);
        const defaultSelectionSet = new Set(defaultSelection);
        tags.forEach((tag) => {
          if (tag.id && defaultSelectionSet.has(tag.id)) {
            changeTag(tag, true);
          }
        });
      }
    }, [changeTag, defaultSelection, hasSetDefault, tags]);

    /* ===== Delete ===== */
    const onDeleteCancelled = useCallback(() => {
      setDeletingTag(null);
    }, []);

    const deleteTagApiCall = useDeleteTagCall({
      orgId: deletingTag?.orgId,
      tagId: deletingTag?.id,
    });

    const onDeleteConfirmed = useCallback(async () => {
      setApiError(null);

      let result;

      try {
        result = await deleteTagApiCall();
      } catch (err) {
        setApiError('There was a problem deleting this tag. Please try again.');
        setDeletingTag(null);
        return;
      }

      if (result?.success) {
        const updatedResponse: StructsGetTagsResponse = {
          tags: structuredClone(tagsUnprocessed).filter((tag) => tag.id !== deletingTag?.id),
        };
        setDeletingTag(null);
        mutate(updatedResponse, { revalidate: false });
        changeTag(deletingTag!, false);
        return;
      }

      setApiError('There was a problem deleting this tag. Please try again.');
      setDeletingTag(null);
    }, [changeTag, deleteTagApiCall, deletingTag, mutate, tagsUnprocessed]);

    /* ===== Rename/Add ===== */
    const [anchor, setAnchor] = useState<any | null>(null);
    const [tagEditor, setTagEditor] = useState<any | null>(null);

    const triggerTagNameEditor = useRef<TriggerRef>(null);

    const cancelEditor = useCallback(() => {
      setApiError('');
      if (addingTag) {
        setAddingTag(false);
      } else {
        setEditingTag(null);
      }
    }, [addingTag]);

    const context = useFloatingRootContext({
      open: editingTag !== null || addingTag,
      onOpenChange: async (_open, _event, reason) => {
        if (reason === 'escape-key') {
          cancelEditor();
        } else if (reason === 'outside-press') {
          if (triggerTagNameEditor.current) {
            triggerTagNameEditor.current.trigger();
          }
        }
      },
      elements: {
        reference: anchor,
        floating: tagEditor,
      },
    });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'dialog' });

    const { getReferenceProps, getFloatingProps } = useInteractions([role, dismiss]);

    const onSavedTag = useCallback<(updatedTag: TagManagerTag) => void>(
      (updatedTag) => {
        const updatedTags = structuredClone(tagsUnprocessed);

        if (addingTag) {
          updatedTags.push(updatedTag);
        } else {
          updatedTags[tagsUnprocessed.findIndex((t) => t.id === updatedTag.id)] = updatedTag;
        }

        // TODO: See comment above this figma link: https://www.figma.com/design/dbDdbDAIJQTYtI2ZoQomij/Hyper-Dashboard-GA-H1'24?node-id=13646-64739&m=dev
        // Consider 1s purple background and checkbox focus

        const updatedResponse: StructsGetTagsResponse = { tags: updatedTags };
        mutate(updatedResponse, { revalidate: false });

        if (addingTag) {
          changeTag(updatedTag, true);
        }

        cancelEditor();
      },
      [addingTag, cancelEditor, changeTag, mutate, tagsUnprocessed]
    );

    /* ===== Selections ===== */

    // When pendingLink changes, update our caller with the new selected tags
    useEffect(() => {
      if (tagSelectMode && onTagSelectionChanged) {
        onTagSelectionChanged([...pendingLink.keys()]);
      }
    }, [onTagSelectionChanged, pendingLink, tagSelectMode]);

    const onTagSelectChanged = useCallback(
      (e: ChangeEvent<HTMLInputElement>, tag: TagManagerTag) => {
        if (!tag.id) {
          return;
        }
        setDebouncingChanges((prev) => [...prev, () => changeTag(tag, e.target.checked)]);
      },
      [changeTag]
    );

    /* ===== Saving ===== */

    const linkTagsCall = useBulkTagCall({ action: 'link', organizationId });
    const unlinkTagsCall = useBulkTagCall({
      action: 'unlink',
      organizationId,
    });

    const flushQueue = useCallback(
      async (
        queue: Map<TagId, TagManagerTag>,
        apiCall: typeof unlinkTagsCall | typeof linkTagsCall
      ) => {
        let result: StructsBulkTagSimsResponse | undefined;
        try {
          result = await apiCall([...selectedSimIds.keys()], [...queue.keys()]);
        } catch (err) {
          if (onError) {
            onError(`There was a problem updating tags: ${err}`);
          }
          return false;
        }

        if (!result?.success && onError) {
          onError(result?.error ?? 'Unknown error');

          return false;
        }

        return true;
      },
      [onError, selectedSimIds]
    );

    const onSubmit = useCallback(async () => {
      const actions: Array<Promise<boolean>> = [];

      if (!singleSimMode && debouncingChanges.length > 0) {
        return;
      }

      if (pendingLink.size === 0 && pendingUnlink.size === 0) {
        return;
      }

      if (onProcessing) {
        onProcessing(true);
      }

      const updatedResponse: StructsGetTagsResponse = { tags: structuredClone(tagsUnprocessed) };

      if (pendingLink.size > 0) {
        actions.push(flushQueue(pendingLink, linkTagsCall));

        updatedResponse.tags = updatedResponse.tags?.map((tag) =>
          tag.id && pendingLink.has(tag.id)
            ? {
                ...tag,
                selectedSimCount: selectedSimIds.size,
              }
            : tag
        );
      }

      if (pendingUnlink.size > 0) {
        actions.push(flushQueue(pendingUnlink, unlinkTagsCall));

        updatedResponse.tags = updatedResponse.tags?.map((tag) =>
          tag.id && pendingUnlink.has(tag.id)
            ? {
                ...tag,
                selectedSimCount: 0,
              }
            : tag
        );
      }

      setPendingUnlink(new Map());
      setPendingLink(new Map());
      mutate(updatedResponse, { revalidate: false });

      const results = await Promise.all(actions);

      if (onProcessing) {
        onProcessing(false);
      }

      if (results.some((result) => !result)) {
        return;
      }

      if (onSuccess) {
        onSuccess([...selectedSimIds.values()]);
      }
    }, [
      singleSimMode,
      debouncingChanges.length,
      pendingLink,
      pendingUnlink,
      onProcessing,
      tagsUnprocessed,
      mutate,
      onSuccess,
      flushQueue,
      linkTagsCall,
      selectedSimIds,
      unlinkTagsCall,
    ]);

    const flushDebounceToPending = useCallback(() => {
      if (debouncingChanges.length > 0) {
        for (
          let tagChange = debouncingChanges.shift();
          tagChange !== undefined;
          tagChange = debouncingChanges.shift()
        ) {
          tagChange();
        }
      }
    }, [debouncingChanges]);

    useEffect(() => {
      if (singleSimMode && (pendingLink.size > 0 || pendingUnlink.size > 0)) {
        onSubmit();
      }
      // Only run on changes to pending link or unlink
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingLink, pendingUnlink]);

    // TODO: Re-enable this if sort changes should be based off of selection
    useDebounceCallback(flushDebounceToPending, singleSimMode ? 500 : 0);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit,
      }),
      [onSubmit]
    );

    /* ===== Render ===== */

    const tableContents = useMemo(() => {
      if (error) {
        return (
          <PanelNotification
            variant="error"
            label="We ran into an issue loading your Tags"
            actions={
              <Button
                variant="secondary"
                iconStart="Sync"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload page
              </Button>
            }
          >
            We will keep trying in the background or you can try to reload the page.
          </PanelNotification>
        );
      }
      if (isLoading) {
        return <PanelNotification>Loading...</PanelNotification>;
      }

      const headers: Array<HeaderItem> = [];

      if (hasSelectedSims || tagSelectMode) {
        headers.push({ children: '', key: 'checkboxes-actions', width: 24 });
      }

      headers.push({ children: 'Tag', key: 'tag' });

      if (!singleSimMode) {
        headers.push({ children: 'SIMs with tag', key: 'sims-with-tag' });
      }

      headers.push({ children: '', key: 'tag-actions' });

      const rows: Array<TableRowItem> = filteredTags.map((tag) => {
        if (!tag?.id) {
          return { key: '' };
        }

        const { id, name, selectedSimCount: selectedSimCountOnTag = 0 } = tag;

        let selectedSimCount = selectedSimCountOnTag;

        if (!tagSelectMode) {
          if (pendingLink.has(id)) {
            selectedSimCount = selectedSimIds.size;
          } else if (pendingUnlink.has(id)) {
            selectedSimCount = 0;
          }
        }

        const editing = editingTag?.id === id;
        const deleting = deletingTag?.id === id;

        const cells: Array<TableCellItem> = [];
        const rowKey = `tag-${id}`;

        const defaultCheckedState = getTagCheckedState(tag, selectedSimIds);

        if (hasSelectedSims || tagSelectMode) {
          cells.push({
            key: `${rowKey}-checkbox`,
            children: (
              <Checkbox
                disabled={editing}
                defaultChecked={
                  defaultCheckedState === CheckedState.Checked || pendingLink.has(tag.id)
                }
                indeterminate={!tagSelectMode && defaultCheckedState === CheckedState.Indeterminate}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onTagSelectChanged(e, tag)}
                label=""
                className={styles.checkbox}
              />
            ),
          });
        }

        cells.push({
          key: `${rowKey}-tag`,
          children: (
            <Popover
              id={`${rowKey}-popover`}
              content={<span className={styles.tagPopoverContents}>{name}</span>}
              tabIndex={-1}
            >
              <Chip
                iconStart="Tag"
                colorVariant={
                  ((defaultCheckedState === CheckedState.Checked && !pendingUnlink.has(tag.id)) ||
                    pendingLink.has(tag.id)) &&
                  selectedSimIds.size > 0 &&
                  !tagSelectMode
                    ? 'lime'
                    : 'default'
                }
                className={styles.tag}
                size={hasSelectedSims ? 'small' : 'medium'}
              >
                {name}
              </Chip>
            </Popover>
          ),
        });

        if (!singleSimMode) {
          const selectedSimCountText =
            defaultCheckedState === CheckedState.Checked ||
            (pendingLink.has(tag.id) && !tagSelectMode && selectedSimIds.size > 0)
              ? `${selectedSimCount} (all)`
              : selectedSimCount.toString();
          cells.push({
            key: `${rowKey}-sims-with-tag`,
            children: (
              <span
                className={clsx(styles.count, (selectedSimCount ?? 0) === 0 && styles.countZero)}
              >
                {selectedSimCount > 0 && !hasPreflight && variant === 'standalone' ? (
                  <Link to={`${activeSimsUrl}?tags=${tag.id}`} type="standalone">
                    {selectedSimCountText}
                  </Link>
                ) : (
                  selectedSimCountText
                )}
              </span>
            ),
          });
        }

        cells.push({
          key: `${rowKey}-actions`,
          children: (
            <div className={styles.actions}>
              {selectedSimCount > 0 && hasPreflight && variant === 'standalone' && (
                <div className={styles.subActions}>
                  <Button
                    to={`${predeploymentSimsUrl}?tags=${tag.id}`}
                    variant="tertiary"
                    size="small"
                    iconEnd="ArrowEastShort"
                    name={`predeployment-sims-tag-${id}`}
                    className={styles.actionButton}
                    disabled={deletingTag !== null}
                  >
                    Pre-deployment SIMs
                  </Button>
                  <Button
                    to={`${activeSimsUrl}?tags=${tag.id}`}
                    variant="tertiary"
                    size="small"
                    iconEnd="ArrowEastShort"
                    name={`active-sims-tag-${id}`}
                    className={styles.actionButton}
                    disabled={deletingTag !== null}
                  >
                    Active SIMs
                  </Button>
                </div>
              )}
              {!editing && canEdit && (
                <div className={styles.subActions}>
                  <Button
                    onClick={() => {
                      setEditingTag(tag);
                      if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Rename');
                    }}
                    variant="tertiary"
                    size="small"
                    iconStart="Pencil"
                    name={`rename-tag-${id}`}
                    className={styles.actionButton}
                    disabled={deletingTag !== null}
                  >
                    Rename
                  </Button>
                  <Button
                    onClick={() => {
                      setDeletingTag(tag);
                      if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Delete');
                    }}
                    variant="tertiary"
                    size="small"
                    iconStart="Trash"
                    className={styles.actionButton}
                    disabled={deletingTag !== null}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ),
        });

        if (editing) {
          return {
            key: rowKey,
            cells,
            className: clsx(styles.row, styles.editing),
            ref: setAnchor as any,
            ...getReferenceProps(),
          };
        }
        return {
          key: rowKey,
          cells,
          className: clsx(styles.row, deleting && styles.deleting),
        };
      });

      const rowsToUse = addingTag
        ? [
            {
              key: 'tag-add-new',
              cells: [
                {
                  key: `add-tag-checkbox`,
                  children: (
                    <Checkbox disabled defaultChecked label="" className={styles.checkbox} />
                  ),
                },
              ],
              className: clsx(styles.row, styles.editing),
              ref: setAnchor as any,
              ...getReferenceProps(),
            },
            ...rows,
          ]
        : rows;

      if (!addingTag && tags.length === 0) {
        return (
          <PanelNotification
            icon={<Pictogram name="Tag" />}
            label="Create your first tag"
            actions={
              <Button
                iconStart="Plus"
                onClick={() => {
                  setAddingTag(true);
                  if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Create tag');
                }}
              >
                Create a tag
              </Button>
            }
          >
            Tags help organize groups of SIMs within your organization for filtering and reporting.
          </PanelNotification>
        );
      }

      return (
        <div className={styles.tableWrapper}>
          <Table
            headers={headers}
            wrapperClassName={styles.table}
            theadClassName={styles.header}
            rows={rowsToUse}
          />
          {tags.length > 0 && filteredTags.length === 0 && (
            <div className={styles.emptyState}>No matching tags found</div>
          )}
          {(editingTag !== null || addingTag) && (
            <TagEditor
              tag={editingTag ?? undefined}
              onCancel={cancelEditor}
              onSave={onSavedTag}
              onError={setApiError}
              rootContext={context}
              setTagEditor={setTagEditor}
              padLeft={hasSelectedSims || tagSelectMode}
              organizationId={organizationId}
              usedTagNames={usedTagNames}
              deviceIds={deviceIds}
              selectedSimIds={selectedSimIds}
              {...getFloatingProps({ ref: triggerTagNameEditor as any })}
            />
          )}
        </div>
      );
    }, [
      error,
      isLoading,
      hasSelectedSims,
      tagSelectMode,
      singleSimMode,
      filteredTags,
      addingTag,
      getReferenceProps,
      tags.length,
      editingTag,
      cancelEditor,
      onSavedTag,
      context,
      organizationId,
      usedTagNames,
      deviceIds,
      selectedSimIds,
      getFloatingProps,
      pendingLink,
      pendingUnlink,
      deletingTag,
      hasPreflight,
      variant,
      predeploymentSimsUrl,
      activeSimsUrl,
      canEdit,
      onTagSelectChanged,
      sendAnalyticsEvent,
    ]);

    return (
      <div
        className={clsx(styles.wrapper, className, variant === 'standalone' && styles.standalone)}
      >
        {apiError && <BannerNotification>{apiError}</BannerNotification>}
        {tags.length > 0 && (
          <div className={styles.topWrapper}>
            <div className={styles.searchWrapper}>
              <TextInput
                id="tag-search"
                type="text"
                placeholder="Search tags..."
                defaultValue={searchText}
                inputProps={{ autoComplete: 'off' }}
                iconStart={<Icon name="Search" size="medium" />}
                {...searchFormRegister('searchText')}
              />
              {searchText.length > 0 && (
                <Button
                  className={styles.clearButton}
                  iconStart={
                    <Icon name="Close" size="medium" fill="DdsColorTextPrimaryOndefault" />
                  }
                  variant="tertiary"
                  onClick={() => searchFormReset()}
                />
              )}
            </div>
            {canEdit && (
              <Button
                className={styles.addButton}
                iconStart="Tag"
                variant="secondary"
                onClick={() => {
                  setAddingTag(true);
                  if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Add new tag');
                }}
              >
                Add a new tag
              </Button>
            )}
          </div>
        )}
        {tableContents}
        {deletingTag && (
          <TagDeleteConfirmation
            tag={deletingTag}
            onConfirmed={onDeleteConfirmed}
            onCancelled={onDeleteCancelled}
          />
        )}
      </div>
    );
  }
);

export default TagManager;
